import { authClient } from '~~/layers/base/lib/auth-client'

/**
 * Reacts to a Better Auth session going invalid mid-use in the Capacitor app (TK-9807): a
 * previously valid session token expiring or being revoked while the SPA is still running.
 *
 * Capacitor-only by design (scope decision) — no-ops on the web storefront.
 */

let isHandlingSessionExpiry = false

/**
 * Asks Better Auth whether the session is *actually* gone.
 *
 * A 401 from our own API is only a hint — it is also how we report an ownership violation, a
 * guest missing a `profile_id`, a missing `profile` row, and (through `finalize-cart`) an upstream
 * ACCS failure forwarded verbatim. Signing a user out on that inference would drop someone with a
 * perfectly valid session out of checkout. So we verify the condition instead of guessing at it.
 *
 * Runs through `authClient`, which has its own fetch pipeline and does not go through
 * `useApiFetch` — so this cannot re-enter the 401 handler that called us.
 *
 * Only answers true on a *successful* response that reports no user. Ambiguity must never cost
 * someone their session, and there are two separate ways to be ambiguous here:
 * - `@better-fetch` resolves to `{ data: null, error }` rather than throwing (the client does not
 *   set `throw: true`), so an errored check is **not** caught below — `data` is null exactly as it
 *   would be for a genuinely absent session. Reading `data` alone would sign users out whenever
 *   `get-session` 500s or the network drops, which is precisely when this must stay quiet.
 * - A thrown exception, for anything better-fetch does not model.
 *
 * Note: if `session.cookieCache` is ever enabled server-side, this should pass
 * `query: { disableCookieCache: true }` so a cached cookie cannot mask a revoked session.
 */
async function isBetterAuthSessionGone(): Promise<boolean> {
    try {
        const { data, error } = await authClient.getSession()
        if (error) {
            return false
        }
        return !data?.user
    } catch {
        return false
    }
}

/**
 * Hands off to the ordinary logout path once expiry is confirmed. `logoutAfterBetterAuth` already
 * ends the session, wipes the account's client caches (TK-12970), restores a guest session, and —
 * on Capacitor — resets the nav stacks and returns to `/`, which is exactly what an expired
 * session needs. Nothing expiry-specific is required here beyond deciding *whether* to call it.
 *
 * Guarded by a module-level flag so several in-flight requests failing at once (realistic: they
 * share the one dead session) run a single logout cycle instead of racing `signOut` against
 * `signIn.anonymous`.
 *
 * Never rejects: callers fire this and walk away (`void handleSessionExpired()`) so the failing
 * request's own error can surface immediately. `logoutAfterBetterAuth` re-throws after reporting
 * to Datadog, and a failing sign-out is most likely exactly when the network is already flaky —
 * so letting that escape here would turn every such case into an unhandled promise rejection.
 */
export async function handleSessionExpired(): Promise<void> {
    if (isHandlingSessionExpiry) {
        return
    }
    isHandlingSessionExpiry = true
    try {
        const runtimeConfig = useRuntimeConfig() as {
            public?: { NUXT_CAPACITOR_APP?: boolean }
        }
        if (!runtimeConfig.public?.NUXT_CAPACITOR_APP) {
            return
        }
        // Resolved before the first await so the Nuxt context is still guaranteed available.
        const { logoutAfterBetterAuth } = useBetterAuthLogout()
        if (!(await isBetterAuthSessionGone())) {
            // Session is alive — the 401 came from something else. Leave the caller's error to
            // surface normally.
            return
        }
        await logoutAfterBetterAuth()
    } catch {
        // Already reported by `logoutAfterBetterAuth`; swallowed so the fire-and-forget call site
        // cannot produce an unhandled rejection.
    } finally {
        isHandlingSessionExpiry = false
    }
}
